// Cookie Policy content in English and Spanish
import type { LocalizedLegalContent } from './types';

export const cookiePolicy: LocalizedLegalContent = {
  en: {
    title: 'StartUs Cookie Policy',
    lastUpdated: 'April 16, 2026',
    introParagraphs: [
      'This Cookie Policy explains how StartUs ("Company", "we", "us", or "our") uses cookies and similar tracking technologies when you visit or use the StartUs platform ("Platform"). By using the Platform, you consent to the use of cookies as described in this policy.',
    ],
    sections: [
      {
        title: '1. What Are Cookies',
        paragraphs: [
          'Cookies are small text files placed on your device (computer, tablet, or mobile) when you visit a website. They are widely used to make websites work efficiently and to provide information to site owners.',
          'Similar technologies such as web beacons, pixels, and local storage may also be used and are covered by this policy.',
        ],
      },
      {
        title: '2. Types of Cookies We Use',
        paragraphs: ['We use the following categories of cookies:'],
        items: [
          'Strictly Necessary Cookies — Required for the Platform to function. They enable core features such as authentication, session management, and security. These cannot be disabled.',
          'Analytics Cookies — Help us understand how users interact with the Platform (pages visited, time spent, errors encountered). This data is aggregated and anonymized.',
          'Preference Cookies — Remember your settings and choices (e.g., language preference, UI preferences) to personalize your experience.',
          'Performance Cookies — Used to monitor Platform performance and improve load times and reliability.',
        ],
      },
      {
        title: '3. How We Use Cookies',
        items: [
          'Authenticate and keep you logged in securely',
          'Remember your language and display preferences',
          'Analyze usage patterns to improve the Platform',
          'Detect and prevent fraudulent or unauthorized activity',
          'Measure the effectiveness of features and improvements',
        ],
      },
      {
        title: '4. Third-Party Cookies',
        paragraphs: [
          'Some cookies are placed by third-party services we use (e.g., analytics providers). These third parties have their own privacy and cookie policies. We do not control third-party cookies.',
        ],
        items: [
          'Analytics providers (e.g., usage tracking tools)',
          'Authentication providers (e.g., Google OAuth)',
        ],
      },
      {
        title: '5. Managing Cookies',
        paragraphs: ['You can control and manage cookies in several ways:'],
        items: [
          "Browser settings — Most browsers allow you to block or delete cookies. Refer to your browser's help documentation for instructions.",
          'Opt-out tools — Some analytics providers offer opt-out mechanisms (e.g., Google Analytics opt-out browser add-on).',
          'Platform settings — Where available, you can manage preferences directly within the Platform.',
        ],
        trailing:
          'Note: Disabling strictly necessary cookies will prevent the Platform from functioning correctly and may result in loss of access to authenticated features.',
      },
      {
        title: '6. Cookie Retention',
        items: [
          'Session cookies — Deleted when you close your browser',
          'Persistent cookies — Remain on your device for a set period (typically up to 12 months) or until manually deleted',
          'Authentication tokens — Expire according to your session settings',
        ],
      },
      {
        title: '7. Changes to This Policy',
        paragraphs: [
          'We may update this Cookie Policy from time to time to reflect changes in technology, law, or our practices. We will notify you of material changes by updating the "Last Updated" date at the top of this page.',
        ],
      },
      {
        title: '8. Contact Us',
        paragraphs: ['If you have questions about our use of cookies, please contact us at:'],
        items: ['Email: privacy@startus.com'],
      },
    ],
  },
  es: {
    title: 'Política de Cookies de StartUs',
    lastUpdated: '16 de abril de 2026',
    introParagraphs: [
      'Esta Política de Cookies explica cómo StartUs ("Empresa", "nosotros" o "nuestro") utiliza cookies y tecnologías de seguimiento similares cuando visitas o usas la plataforma StartUs ("Plataforma"). Al usar la Plataforma, consientes el uso de cookies según lo descrito en esta política.',
    ],
    sections: [
      {
        title: '1. ¿Qué Son las Cookies?',
        paragraphs: [
          'Las cookies son pequeños archivos de texto que se colocan en tu dispositivo (computadora, tableta o móvil) cuando visitas un sitio web. Se utilizan ampliamente para hacer que los sitios web funcionen de manera eficiente y para proporcionar información a los propietarios del sitio.',
          'También pueden usarse tecnologías similares como balizas web, píxeles y almacenamiento local, que están cubiertas por esta política.',
        ],
      },
      {
        title: '2. Tipos de Cookies que Usamos',
        paragraphs: ['Utilizamos las siguientes categorías de cookies:'],
        items: [
          'Cookies Estrictamente Necesarias — Requeridas para el funcionamiento de la Plataforma. Habilitan funciones principales como autenticación, gestión de sesiones y seguridad. No pueden desactivarse.',
          'Cookies de Analítica — Nos ayudan a comprender cómo los usuarios interactúan con la Plataforma (páginas visitadas, tiempo de uso, errores encontrados). Los datos son agregados y anonimizados.',
          'Cookies de Preferencias — Recuerdan tus configuraciones y elecciones (p. ej., idioma, preferencias de interfaz) para personalizar tu experiencia.',
          'Cookies de Rendimiento — Se utilizan para monitorear el rendimiento de la Plataforma y mejorar los tiempos de carga y la fiabilidad.',
        ],
      },
      {
        title: '3. Cómo Usamos las Cookies',
        items: [
          'Autenticarte y mantener tu sesión iniciada de forma segura',
          'Recordar tus preferencias de idioma e interfaz',
          'Analizar patrones de uso para mejorar la Plataforma',
          'Detectar y prevenir actividad fraudulenta o no autorizada',
          'Medir la efectividad de funciones y mejoras',
        ],
      },
      {
        title: '4. Cookies de Terceros',
        paragraphs: [
          'Algunas cookies son colocadas por servicios de terceros que utilizamos (p. ej., proveedores de analítica). Estos terceros tienen sus propias políticas de privacidad y cookies. No controlamos las cookies de terceros.',
        ],
        items: [
          'Proveedores de analítica (p. ej., herramientas de seguimiento de uso)',
          'Proveedores de autenticación (p. ej., Google OAuth)',
        ],
      },
      {
        title: '5. Gestión de Cookies',
        paragraphs: ['Puedes controlar y gestionar las cookies de varias maneras:'],
        items: [
          'Configuración del navegador — La mayoría de los navegadores permiten bloquear o eliminar cookies. Consulta la documentación de ayuda de tu navegador.',
          'Herramientas de exclusión — Algunos proveedores de analítica ofrecen mecanismos de exclusión (p. ej., complemento de exclusión de Google Analytics).',
          'Configuración de la Plataforma — Donde esté disponible, puedes gestionar preferencias directamente desde la Plataforma.',
        ],
        trailing:
          'Nota: Deshabilitar las cookies estrictamente necesarias impedirá el correcto funcionamiento de la Plataforma y puede resultar en la pérdida de acceso a funciones autenticadas.',
      },
      {
        title: '6. Retención de Cookies',
        items: [
          'Cookies de sesión — Se eliminan cuando cierras tu navegador',
          'Cookies persistentes — Permanecen en tu dispositivo durante un período determinado (generalmente hasta 12 meses) o hasta que las elimines manualmente',
          'Tokens de autenticación — Expiran según la configuración de tu sesión',
        ],
      },
      {
        title: '7. Cambios en Esta Política',
        paragraphs: [
          'Podemos actualizar esta Política de Cookies periódicamente para reflejar cambios en tecnología, legislación o nuestras prácticas. Te notificaremos sobre cambios importantes actualizando la fecha de "Última actualización" en la parte superior de esta página.',
        ],
      },
      {
        title: '8. Contáctanos',
        paragraphs: ['Si tienes preguntas sobre nuestro uso de cookies, contáctanos en:'],
        items: [
          'Correo electrónico: privacy@startus.com',
          'Dirección: StartUs, [Dirección de la Empresa]',
        ],
      },
    ],
  },
};
